import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gift, Send, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const EMPTY = {
  numero_dossier: "",
  nom_prenom: "",
  partenaire: "",
  experience: "",
  date_debut: "",
  date_fin: "",
  confirme_prereservation: false,
};

export default function RecevoirBon() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...EMPTY, numero_dossier: params.get("dossier") || "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const up = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bons", form);
      setSent(true);
    } catch (err) {
      toast.error("Envoi impossible", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-pblv-bg" data-testid="recevoir-bon-page">
      <header className="mx-auto flex max-w-2xl items-center justify-between px-6 py-5">
        <Link to="/" data-testid="bon-back-home" className="inline-flex items-center gap-2 text-sm font-semibold text-pblv-mauve hover:underline">
          <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
        </Link>
        <img src="/logo-tpoz.png" alt="T'POZ" className="h-14 w-auto" />
      </header>

      <main className="mx-auto max-w-2xl px-6 pb-20">
        <div className="rounded-3xl border border-pblv-ink/10 bg-white p-6 shadow-sm sm:p-10">
          {sent ? (
            <div data-testid="bon-success" className="flex flex-col items-center gap-4 py-8 text-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <Check className="h-7 w-7" strokeWidth={2} />
              </span>
              <h1 className="font-serif text-2xl font-medium text-pblv-ink">Merci.</h1>
              <p className="max-w-md text-sm leading-relaxed text-pblv-inksoft">
                Votre Bon T'POZ vous sera transmis sous 5 jours. Présentez-le au partenaire pour confirmer votre
                réservation.
              </p>
              <Link
                to="/"
                data-testid="bon-success-home"
                className="mt-2 rounded-full bg-pblv-mauve px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-pblv-mauvedark"
              >
                Retour à l'accueil
              </Link>
            </div>
          ) : (
            <>
              <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-pblv-mauve/12 text-pblv-mauve">
                <Gift className="h-6 w-6" strokeWidth={1.6} />
              </span>
              <h1 className="font-serif text-3xl font-medium text-pblv-ink sm:text-4xl">Recevoir mon Bon T'POZ</h1>
              <p className="mt-2 text-sm leading-relaxed text-pblv-inksoft">
                Votre demande a été validée et vous avez pré-réservé auprès d'un partenaire ? Indiquez-nous les
                informations ci-dessous pour recevoir votre Bon.
              </p>

              <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="b-dossier">Numéro de dossier *</Label>
                  <Input id="b-dossier" data-testid="bon-dossier" required value={form.numero_dossier} onChange={up("numero_dossier")} placeholder="Ex. : TPOZ-A1B2C3" />
                  <p className="text-xs text-pblv-inksoft">Communiqué par l'association.</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="b-nom">Nom et prénom *</Label>
                  <Input id="b-nom" data-testid="bon-nom" required value={form.nom_prenom} onChange={up("nom_prenom")} placeholder="Votre nom et prénom" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="b-partenaire">Partenaire choisi *</Label>
                  <Input id="b-partenaire" data-testid="bon-partenaire" required value={form.partenaire} onChange={up("partenaire")} placeholder="Nom de l'hébergeur ou du partenaire bien-être" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="b-experience">Expérience choisie *</Label>
                  <Input id="b-experience" data-testid="bon-experience" required value={form.experience} onChange={up("experience")} placeholder="Ex. : Séjour 2 nuits, massage bien-être…" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Date(s) *</Label>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-pblv-inksoft">Du</span>
                      <Input type="date" data-testid="bon-date-debut" required value={form.date_debut} onChange={up("date_debut")} />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-pblv-inksoft">au</span>
                      <Input type="date" data-testid="bon-date-fin" required value={form.date_fin} onChange={up("date_fin")} />
                    </div>
                  </div>
                </div>
                <label className="flex items-start gap-3 rounded-xl bg-pblv-bg p-3 text-sm text-pblv-ink">
                  <input
                    type="checkbox"
                    data-testid="bon-prereservation"
                    required
                    checked={form.confirme_prereservation}
                    onChange={up("confirme_prereservation")}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-pblv-mauve"
                  />
                  Je certifie avoir effectué une pré-réservation auprès de ce partenaire. *
                </label>
                <button
                  type="submit"
                  data-testid="bon-submit"
                  disabled={submitting}
                  className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-60"
                >
                  <Send className="h-4 w-4" strokeWidth={1.8} />
                  {submitting ? "Envoi en cours…" : "Recevoir mon Bon"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
